import React, { type ReactNode, useId, useMemo } from 'react';
import { isMobile } from 'react-device-detect';

import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { type Platform, useUsersOS } from '../../utilities/useUsersOS';

type ShortcutVariants = 'destructive' | 'neutral' | 'primary';
export type EffectiveShortcutKey = SpecialKey | string;
export type ShortcutKey = EffectiveShortcutKey | ReactNode;

export enum SpecialKey {
  Alt = 'alt',
  Backspace = 'backspace',
  Click = 'click',
  ClickOrEnter = 'clickOrEnter',
  CmdOrCtrl = 'cmdOrCtrl',
  Ctrl = 'ctrl',
  Escape = 'escape',
  Enter = 'enter',
  Meta = 'meta',
  Shift = 'shift',
}

const ShortcutKeyContainer = styled(Text, {
  display: 'inline-flex',
  width: 'fit-content',
  fontFamily: '$mono',
  fontSize: '$11',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: '$bold',
  lineHeight: '$12',
  whiteSpace: 'nowrap',
  color: '$$keyColor',
});
ShortcutKeyContainer.displayName = 'ShortcutKeyContainer';

function AltKey({ platform }: { platform: Platform }) {
  if (platform === 'mac') {
    return '⌥';
  }
  return 'Alt';
}

function BackspaceKey() {
  return '⌫';
}

function ClickKey() {
  return 'Click';
}

function ClickOrEnterKey() {
  return 'Click/⏎';
}

function CmdOrCtrlKey({ platform }: { platform: Platform }) {
  if (platform === 'mac') {
    return '⌘';
  }
  return 'Ctrl';
}

function CtrlKey({ platform }: { platform: Platform }) {
  if (platform === 'mac') {
    return '^';
  }
  return 'Ctrl';
}

function EnterKey() {
  return '⏎';
}

function EscapeKey() {
  return 'Esc';
}

function MetaKey({ platform }: { platform: Platform }) {
  if (platform === 'mac') {
    return '⌘';
  }
  if (platform === 'linux') {
    return 'Super';
  }
  return '⊞';
}

function ShiftKey() {
  return '⇧';
}

function ShortcutKeyDisplay({ children: key }: { children: ShortcutKey }) {
  const platform = useUsersOS();

  const renderedKey = useMemo(() => {
    if (key === SpecialKey.Alt) return <AltKey platform={platform} />;
    if (key === SpecialKey.Backspace) return <BackspaceKey />;
    if (key === SpecialKey.Click) return <ClickKey />;
    if (key === SpecialKey.ClickOrEnter) return <ClickOrEnterKey />;
    if (key === SpecialKey.CmdOrCtrl) return <CmdOrCtrlKey platform={platform} />;
    if (key === SpecialKey.Ctrl) return <CtrlKey platform={platform} />;
    if (key === SpecialKey.Escape) return <EscapeKey />;
    if (key === SpecialKey.Enter) return <EnterKey />;
    if (key === SpecialKey.Meta) return <MetaKey platform={platform} />;
    if (key === SpecialKey.Shift) return <ShiftKey />;

    return key;
  }, [key, platform]);

  return <ShortcutKeyContainer>{renderedKey}</ShortcutKeyContainer>;
}

const ShortcutContainer = styled('div', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '$2',
  padding: '$2 $4',
  borderRadius: '$4',

  variants: {
    control: {
      true: {},
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    variant: {
      destructive: {},
      neutral: {},
      primary: {},
    },
  },

  compoundVariants: [
    // Destructive
    {
      variant: 'destructive',
      control: false,
      css: {
        backgroundColor: colors.tokenBgNegativeLight,
        strokeAll: colors.tokenStrokeNegativeLight,
        $$keyColor: colors.headingNegativeLight,

        [darkThemeSelector]: {
          backgroundColor: colors.tokenBgNegativeDark,
          strokeAll: colors.tokenStrokeNegativeDark,
          $$keyColor: colors.headingNegativeDark,
        },
      },
    },
    // Destructive, control
    {
      variant: 'destructive',
      control: true,
      css: {
        backgroundColor: colors.red700,
        strokeAll: colors.strokeNegativeDark,
        $$keyColor: colors.red50,
      },
    },
    // Neutral
    {
      variant: 'neutral',
      css: {
        backgroundColor: colors.tokenBgNeutralLight,
        strokeAll: colors.tokenStrokeNeutralLight,
        $$keyColor: colors.headingNeutralLight,

        [darkThemeSelector]: {
          backgroundColor: colors.tokenBgNeutralDark,
          strokeAll: colors.tokenStrokeNeutralDark,
          $$keyColor: colors.headingNeutralDark,
        },
      },
    },
    // Internal
    {
      control: false,
      internal: true,
      css: {
        backgroundColor: colors.internalTokenBgLight,
        strokeAll: colors.internalStrokeLight,
        $$keyColor: colors.internalBodyLight,

        [darkThemeSelector]: {
          backgroundColor: colors.internalTokenBgDark,
          strokeAll: colors.internalStrokeDark,
          $$keyColor: colors.internalBodyDark,
        },
      },
    },
    // Primary
    {
      variant: 'primary',
      control: false,
      css: {
        backgroundColor: colors.tokenBgBrandLight,
        strokeAll: colors.tokenStrokeBrandLight,
        $$keyColor: colors.headingBrandLight,

        [darkThemeSelector]: {
          backgroundColor: colors.tokenBgBrandDark,
          strokeAll: colors.tokenStrokeBrandDark,
          $$keyColor: colors.headingBrandDark,
        },
      },
    },
    // Primary, control
    {
      variant: 'primary',
      control: true,
      css: {
        backgroundColor: colors.brand700,
        strokeAll: colors.strokeBrandDark,
        $$keyColor: colors.brand50,
      },
    },
  ],
});
ShortcutContainer.displayName = 'ShortcutContainer';

type ShortcutProps = {
  /**
   * Whether the shortcut is contained inside an Atto control component.
   */
  control?: boolean;
  /**
   * Whether the shortcut is internal only.
   */
  internal?: boolean;
  /**
   * The keys to display in the shortcut.
   */
  keys: ShortcutKey[];
  /**
   * The variant of the shortcut.
   */
  variant?: ShortcutVariants;
};

export function Shortcut({
  control = false,
  internal = false,
  keys,
  variant = 'neutral',
  ...remaining
}: ShortcutProps) {
  const uuid = useId();

  if (isMobile) {
    return null;
  }

  return (
    <ShortcutContainer control={control} internal={internal} variant={variant} {...remaining}>
      {keys.map((key) => (
        <ShortcutKeyDisplay key={`${uuid}-${key}`}>{key}</ShortcutKeyDisplay>
      ))}
    </ShortcutContainer>
  );
}

export default Shortcut;
